// Sticky commit bar for /releases/new/chromium.
//
// Twin of the data-confident profiles footer: stays inside the
// SidebarInset by being position:sticky inside the page's flex
// column, and slides in once we have a resolved version. CTA copy
// flips between "Download latest & publish" and "Start release"
// depending on mode.

import { ArrowRight, Loader2 } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";

const EASE = [0.22, 1, 0.36, 1] as const;

interface Props {
	mode: "latest" | "catalog" | "manual";
	resolvedVersion: string | null;
	alreadyPublished: boolean;
	submitting: boolean;
	canSubmit: boolean;
}

const MODE_LABEL: Record<Props["mode"], string> = {
	latest: "latest",
	catalog: "catalog",
	manual: "manual",
};

export function CommitBar({
	mode,
	resolvedVersion,
	alreadyPublished,
	submitting,
	canSubmit,
}: Props) {
	const reduce = useReducedMotion();
	return (
		<motion.div
			initial={reduce ? false : { opacity: 0, y: 28 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: 20 }}
			transition={{ duration: 0.38, ease: EASE }}
			className="sticky bottom-4 z-30 mx-auto flex w-full max-w-4xl items-center gap-3 rounded-2xl border border-border/80 bg-background/95 px-4 py-3 shadow-xl shadow-foreground/5 backdrop-blur sm:gap-4 sm:px-5 supports-backdrop-filter:bg-background/85"
		>
			<div className="hidden flex-col sm:flex">
				<span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
					Commit
				</span>
				<span className="text-xs text-muted-foreground">Chromium</span>
			</div>

			<div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
				<span className="text-[10px] uppercase tracking-wider text-muted-foreground">
					mode
				</span>
				<span className="font-mono text-xs">{MODE_LABEL[mode]}</span>
				<span className="text-muted-foreground/60">·</span>
				<span className="text-[10px] uppercase tracking-wider text-muted-foreground">
					version
				</span>
				<span className="truncate font-mono text-xs tabular-nums">
					{resolvedVersion ?? "—"}
				</span>
			</div>

			{alreadyPublished ? (
				<span className="hidden text-xs text-destructive lg:inline">
					already published
				</span>
			) : null}

			<button
				type="submit"
				disabled={!canSubmit}
				className={
					canSubmit
						? "group inline-flex h-9 shrink-0 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-all hover:-translate-y-px hover:bg-primary/90 disabled:opacity-50"
						: "inline-flex h-9 shrink-0 items-center gap-2 rounded-lg bg-muted px-4 text-sm font-medium text-muted-foreground"
				}
			>
				{submitting ? <Loader2 className="size-4 animate-spin" /> : null}
				{submitting
					? "Starting"
					: mode === "latest"
						? "Download latest & publish"
						: "Start release"}
				{!submitting ? (
					<ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
				) : null}
			</button>
		</motion.div>
	);
}
